import React, { useState, useEffect } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import {
  NamedLink,
  IconProfile,
  IconSearch,
  IconEdit,
  IconKeys,
  IconGift,
  IconGear,
  IconGlobe,
  IconChat,
  IconLogout,
  IconShoppingBag,
  IconCreditCard,
  IconDollarBag,
  IconBank,
  IconArrowHead,
} from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { showPaymentDetailsForUser } from '../../util/userHelpers';

import css from './ProfileNav.module.css';

const ADMIN_SUB_PAGES = [
  'AdminTransactionsPage',
  'AdminTransactionDetailPage',
  'AdminUsersPage',
  'AdminUserDetailPage',
  'AdminAbandonedCartsPage',
  'AdminNewsletterDashboardPage',
  'AdminNewsletterSubscribersPage',
  'AdminNewsletterCampaignsPage',
  'AdminNewsletterCampaignNewPage',
  'AdminNewsletterCampaignEditPage',
  'AdminNewsletterCampaignDetailPage',
  'AdminNewsletterSegmentsPage',
  'BlogAdminPage',
  'BlogPostEditorNew',
  'BlogPostEditorEdit',
  'CategoriesAdminPage',
  'BrandsAdminPage',
  'AdminLandingPage',
  'CategorySlugsAdminPage',
];

const NEWSLETTER_PAGES = [
  'AdminNewsletterDashboardPage',
  'AdminNewsletterSubscribersPage',
  'AdminNewsletterCampaignsPage',
  'AdminNewsletterCampaignNewPage',
  'AdminNewsletterCampaignEditPage',
  'AdminNewsletterCampaignDetailPage',
  'AdminNewsletterSegmentsPage',
];

const ProfileNavLink = ({ text, selected, linkProps, className, icon: Icon, subLink = false }) => {
  const linkClasses = classNames(
    css.link,
    {
      [css.selectedLink]: selected,
      [css.subLink]: subLink,
    },
    className
  );

  return (
    <NamedLink className={linkClasses} {...linkProps}>
      {Icon && <Icon className={css.linkIcon} />}
      {text}
    </NamedLink>
  );
};

const AdminNavSection = ({ currentPage }) => {
  const isAdminPage = ADMIN_SUB_PAGES.includes(currentPage);
  const [expanded, setExpanded] = useState(isAdminPage);

  useEffect(() => {
    if (isAdminPage) setExpanded(true);
  }, [isAdminPage]);

  const subLinks = [
    {
      text: <FormattedMessage id="UserNav.adminTransactions" />,
      selected:
        currentPage === 'AdminTransactionsPage' || currentPage === 'AdminTransactionDetailPage',
      linkProps: { name: 'AdminTransactionsPage' },
      icon: IconShoppingBag,
    },
    {
      text: <FormattedMessage id="UserNav.adminUsers" />,
      selected: currentPage === 'AdminUsersPage' || currentPage === 'AdminUserDetailPage',
      linkProps: { name: 'AdminUsersPage' },
      icon: IconProfile,
    },
    {
      text: <FormattedMessage id="UserNav.adminAbandonedCarts" />,
      selected: currentPage === 'AdminAbandonedCartsPage',
      linkProps: { name: 'AdminAbandonedCartsPage' },
      icon: IconShoppingBag,
    },
    {
      text: <FormattedMessage id="UserNav.newsletter" defaultMessage="Newsletter" />,
      selected: NEWSLETTER_PAGES.includes(currentPage),
      linkProps: { name: 'AdminNewsletterDashboardPage' },
      icon: IconChat,
    },
    {
      text: <FormattedMessage id="UserNav.contentManagement" />,
      selected:
        currentPage === 'BlogAdminPage' ||
        currentPage === 'BlogPostEditorNew' ||
        currentPage === 'BlogPostEditorEdit',
      linkProps: { name: 'BlogAdminPage' },
      icon: IconEdit,
    },
    {
      text: <FormattedMessage id="UserNav.categories" />,
      selected: currentPage === 'CategoriesAdminPage',
      linkProps: { name: 'CategoriesAdminPage' },
      icon: IconSearch,
    },
    {
      text: <FormattedMessage id="UserNav.brands" defaultMessage="Marques" />,
      selected: currentPage === 'BrandsAdminPage',
      linkProps: { name: 'BrandsAdminPage' },
      icon: IconGlobe,
    },
    {
      text: <FormattedMessage id="UserNav.landing" defaultMessage="Landing" />,
      selected: currentPage === 'AdminLandingPage',
      linkProps: { name: 'AdminLandingPage' },
      icon: IconEdit,
    },
    {
      text: <FormattedMessage id="UserNav.categorySlugs" />,
      selected: currentPage === 'CategorySlugsAdminPage',
      linkProps: { name: 'CategorySlugsAdminPage' },
      icon: IconGlobe,
    },
  ];

  const isOpen = expanded;

  return (
    <div className={css.adminSection}>
      <button
        type="button"
        className={classNames(css.adminToggle, {
          [css.adminToggleOpen]: isOpen,
          [css.adminToggleActive]: isAdminPage,
        })}
        aria-expanded={isOpen}
        onClick={() => setExpanded(prev => !prev)}
      >
        <IconGear className={css.linkIcon} />
        <span className={css.adminToggleLabel}>
          <FormattedMessage id="UserNav.admin" />
        </span>
        <IconArrowHead
          className={css.adminChevron}
          direction={isOpen ? 'up' : 'down'}
          size="small"
        />
      </button>
      {isOpen && (
        <div className={css.adminSubLinks}>
          {subLinks.map((link, index) => (
            <ProfileNavLink key={index} {...link} subLink />
          ))}
        </div>
      )}
    </div>
  );
};

const ProfileNav = props => {
  const { className, currentPage, currentUser, onLogout } = props;
  const classes = classNames(css.root, className);

  const config = useConfiguration();
  const isProvider = currentUser?.attributes?.profile?.publicData?.userType === 'provider';
  const isAdmin = currentUser?.attributes?.profile?.publicData?.userType === 'admin';
  const { showPayoutDetails, showPaymentMethods } = showPaymentDetailsForUser(config, currentUser) || {};

  const links = [
    {
      text: <FormattedMessage id="UserNav.profileDashboard" />,
      selected: currentPage === 'ProfileDashboardPage',
      linkProps: {
        name: 'ProfileDashboardPage',
      },
      icon: IconProfile,
    },
    // Hide Orders for providers
    !isProvider
      ? {
          text: <FormattedMessage id="UserNav.orders" />,
          selected: currentPage === 'OrdersPage' || currentPage === 'OrderDetailsPage',
          linkProps: {
            name: 'OrdersPage',
          },
          icon: IconShoppingBag,
        }
      : null,
    isProvider
      ? {
          text: <FormattedMessage id="UserNav.sales" />,
          selected: currentPage === 'SalesPage' || currentPage === 'SaleDetailsPage',
          linkProps: {
            name: 'SalesPage',
          },
          icon: IconDollarBag,
        }
      : null,
    {
      text: <FormattedMessage id={isProvider ? 'UserNav.sellerRequests' : 'UserNav.myRequests'} />,
      selected:
        currentPage === 'MyRequestsPage' ||
        currentPage === 'NewSupportRequestPage' ||
        currentPage === 'SupportRequestDetailPage',
      linkProps: {
        name: 'MyRequestsPage',
      },
      icon: IconChat,
    },
    !isProvider
      ? {
          text: <FormattedMessage id="UserNav.cagnotteFidelite" />,
          selected: currentPage === 'CagnotteFidelitePage',
          linkProps: {
            name: 'CagnotteFidelitePage',
          },
          icon: IconGift,
        }
      : null,
    // Hidden: PaymentIntentsPage link
    // isProvider
    //   ? {
    //       text: <FormattedMessage id="UserNav.paymentIntents" />,
    //       selected: currentPage === 'PaymentIntentsPage',
    //       linkProps: {
    //         name: 'PaymentIntentsPage',
    //       },
    //     }
    //   : null,
    isProvider
      ? {
          text: <FormattedMessage id="UserNav.catalogue" />,
          selected: currentPage === 'CataloguePage',
          linkProps: {
            name: 'CataloguePage',
          },
          icon: IconSearch,
        }
      : null,
    {
      text: <FormattedMessage id="UserNav.profileSettings" />,
      selected: currentPage === 'ProfileSettingsPage' || currentPage === 'ContactDetailsPage',
      linkProps: {
        name: 'ProfileSettingsPage',
      },
      icon: IconEdit,
    },
  ];

  const settingsLinks = [
    {
      text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.passwordTabTitle" />,
      selected: currentPage === 'PasswordChangePage',
      linkProps: {
        name: 'PasswordChangePage',
      },
      icon: IconKeys,
    },
    showPayoutDetails
      ? {
          text: <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentsTabTitle" />,
          selected: currentPage === 'StripePayoutPage',
          linkProps: {
            name: 'StripePayoutPage',
          },
          icon: IconBank,
        }
      : null,
    showPaymentMethods
      ? {
          text: (
            <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle" />
          ),
          selected: currentPage === 'PaymentMethodsPage',
          linkProps: {
            name: 'PaymentMethodsPage',
          },
          icon: IconCreditCard,
        }
      : null,
  ];

  const visibleMainLinks = links.filter(Boolean);
  const visibleSettingsLinks = settingsLinks.filter(Boolean);

  return (
    <div className={classes}>
      {visibleMainLinks.map((link, index) => (
        <ProfileNavLink key={index} {...link} />
      ))}
      {isAdmin ? <AdminNavSection currentPage={currentPage} /> : null}
      {visibleSettingsLinks.map((link, index) => (
        <ProfileNavLink key={`settings-${index}`} {...link} />
      ))}
      {onLogout && (
        <button type="button" className={css.logoutButton} onClick={onLogout}>
          <IconLogout className={css.logoutIcon} />
          <FormattedMessage id="ProfileDashboardPage.logout" />
        </button>
      )}
    </div>
  );
};

export default ProfileNav;
